import { JSX, useMemo } from "react";

import { Stack, Typography } from "@mui/material";
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    gridStringOrNumberComparator,
    GridValidRowModel,
} from "@mui/x-data-grid";

import {
    ApprovalDataChange,
    ApprovalDataChangeAction,
    ApprovalDataChangeField,
} from "types/ApprovalDataChangeSet";
import { ApprovalDataChangeSetEntityActionDataTitle } from "./ApprovalDataChangeSetEntityActionDataTitle";

export interface ApprovalDataChangeSetEntityActionDataProps {
    entity: string;
    action: ApprovalDataChangeAction;
    changes: ApprovalDataChange[];
}

function RenderGridCell(
    renderCellProps: GridRenderCellParams<
        GridValidRowModel,
        ApprovalDataChangeField
    >,
) {
    const value = renderCellProps.value;

    if (!value) return;

    const elements: JSX.Element[] = [];

    if (value.originalValue?.length && value.originalValue !== value.newValue) {
        elements.push(
            <Typography
                key={`${value.name}-originalValue`}
                component={"span"}
                color={"error"}
                variant="inherit"
                sx={{ textDecoration: "line-through" }}
            >
                {value.originalValue}
            </Typography>,
        );
    }

    if (value.newValue?.length) {
        const asChanged = value.originalValue !== value.newValue;
        elements.push(
            <Typography
                key={`${value.name}-newValue`}
                component={"span"}
                color={asChanged ? "success" : "textPrimary"}
                variant="inherit"
            >
                {value.newValue}
            </Typography>,
        );
    }

    return elements;
}

export function ApprovalDataChangeSetEntityActionData(
    props: ApprovalDataChangeSetEntityActionDataProps,
) {
    const rows = useMemo(() => {
        return props.changes.map((x, i) => {
            const row = {
                id: i,
            };

            const rowData = x.fields.reduce((prev, curr) => {
                return {
                    ...prev,
                    [curr.name]: curr,
                };
            }, row);

            return { ...row, ...rowData };
        });
    }, props.changes);

    const columns = useMemo(() => {
        if (props.changes.length === 0) {
            return [];
        }

        return props.changes[0].fields.map(
            (x) =>
                ({
                    field: x.name,
                    headerName: x.name,
                    editable: false,
                    sortable: true,
                    filterable: false,
                    renderCell: RenderGridCell,
                    getSortComparator: (sortDirection) => {
                        const modifier = sortDirection === "desc" ? -1 : 1;
                        return (
                            value1: ApprovalDataChangeField,
                            value2: ApprovalDataChangeField,
                            cellParams1,
                            cellParams2,
                        ) => {
                            if (value1 === null) {
                                return 1;
                            }
                            if (value2 === null) {
                                return -1;
                            }
                            const value1Data =
                                value1.newValue ?? value1.originalValue;
                            const value2Data =
                                value2.newValue ?? value2.originalValue;
                            return (
                                modifier *
                                gridStringOrNumberComparator(
                                    value1Data,
                                    value2Data,
                                    cellParams1,
                                    cellParams2,
                                )
                            );
                        };
                    },
                }) as GridColDef<(typeof rows)[number]>,
        );
    }, props.changes);

    return (
        <Stack spacing={2}>
            <ApprovalDataChangeSetEntityActionDataTitle
                entity={props.entity}
                action={props.action}
                changeCount={props.changes.length}
            />
            <DataGrid
                rows={rows}
                columns={columns}
                density={"compact"}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 25,
                        },
                    },
                }}
                disableRowSelectionOnClick
            />
        </Stack>
    );
}
